import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import authService from "../services/discordAuth";
import { trackEvent } from "../utils/analytics";
import usePageTitle from "../hooks/usePageTitle";
import { useTranslation, Trans } from "react-i18next";
import "./CheckoutPage.css";

const TIERS = {
  premium: {
    name: "Premium Tier",
    price: 9.99,
    icon: "⭐",
    color: "#ffd700",
  },
  business: {
    name: "Business Tier",
    price: 29.99,
    icon: "🚀",
    color: "#5865f2",
  },
};

function CheckoutPage() {
  const { t } = useTranslation();
  const { tierId } = useParams();
  const navigate = useNavigate();
  usePageTitle(
    t("checkout.pageTitle", {
      tierName: TIERS[tierId]?.name || t("checkout.genericPlural"),
    }),
  );

  const [user, setUser] = useState(null);
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const tier = TIERS[tierId];
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      authService.login();
      return;
    }

    if (!tier) {
      navigate("/pricing");
      return;
    }

    const userData = authService.getUser();
    setUser(userData);

    trackEvent("checkout_start", {
      tier_id: tierId,
      tier_name: tier.name,
    });

    // Verificar se retornou do Stripe com sucesso
    const queryParams = new URLSearchParams(window.location.search);
    const sessionId = queryParams.get("session_id");

    if (sessionId) {
      setSuccess(true);
      return;
    }

    const fetchUserBots = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/user/my-bots?userId=${userData.id}&viewerId=${userData.id}`);
        const data = await response.json();

        if (data.success) {
          const approvedOnly = (data.bots || []).filter((bot) => bot.status === "approved");
          setBots(approvedOnly);
          if (approvedOnly.length > 0) {
            setSelectedBotId(approvedOnly[0].id);
          }
        }
      } catch (err) {
        console.error("Erro ao buscar bots:", err);
        setError(t("checkout.errorLoadingBots"));
      } finally {
        setLoading(false);
      }
    };

    fetchUserBots();
  }, [tierId, navigate, tier, BACKEND_URL, t]);

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!selectedBotId) {
      setError(t("checkout.errorNoBot"));
      return;
    }

    setProcessing(true);
    setError("");

    try {
      const token = authService.getAccessToken();
      const response = await fetch(`${BACKEND_URL}/api/bots/${selectedBotId}/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tier: tierId }),
      });

      const data = await response.json();

      if (data.success && data.url) {
        trackEvent("checkout_redirect_stripe", {
          tier_id: tierId,
          bot_id: selectedBotId,
        });
        // Redireciona para o Stripe Checkout
        window.location.href = data.url;
      } else {
        setError(data.error || t("checkout.errorCheckout"));
      }
    } catch (err) {
      console.error("Erro no checkout:", err);
      setError(t("checkout.errorConnection"));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div className="checkout-loading">{t("checkout.loading")}</div>;

  if (success) {
    return (
      <div className="checkout-success-container">
        <div className="success-card">
          <div className="success-icon">✅</div>
          <h2>{t("checkout.successTitle")}</h2>
          <p>
            <Trans
              i18nKey="checkout.successDesc"
              values={{ tierName: tier.name }}
              defaults="O plano <0>{{tierName}}</0> foi ativado com sucesso para o seu bot."
              components={{ 0: <strong /> }}
            />
          </p>
          <p className="redirect-text">{t("checkout.redirecting")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-wrapper">
          {/* Coluna da Esquerda: Plan Info */}
          <div className="checkout-sidebar">
            <div className="checkout-card plan-summary">
              <h3>{t("checkout.orderSummary")}</h3>
              <div className="selected-plan-box" style={{ borderColor: tier.color }}>
                <span className="plan-icon">{tier.icon}</span>
                <div className="plan-details">
                  <span className="plan-name">{tier.name}</span>
                  <span className="plan-price">
                    ${tier.price}
                    {t("checkout.perMonth")}
                  </span>
                </div>
              </div>

              <div className="checkout-divider"></div>

              <div className="price-breakdown">
                <div className="price-row">
                  <span>{t("checkout.subtotal")}</span>
                  <span>${tier.price}</span>
                </div>
                <div className="price-row">
                  <span>{t("checkout.taxes")}</span>
                  <span>$0.00</span>
                </div>
                <div className="price-row total">
                  <span>{t("checkout.totalToday")}</span>
                  <span>${tier.price}</span>
                </div>
              </div>

              <div className="checkout-security-info">
                <p>{t("checkout.securePayment")}</p>
              </div>
            </div>
          </div>

          {/* Coluna da Direita: Form */}
          <div className="checkout-main">
            <div className="checkout-card">
              <h2>{t("checkout.finishPurchase")}</h2>
              <p className="subtitle">{t("checkout.selectBotSubtitle")}</p>

              <form onSubmit={handleCheckout}>
                <div className="form-group">
                  <label>{t("checkout.step1")}</label>
                  {bots.length > 0 ? (
                    <>
                      <div className="bot-selector-grid">
                        {bots.map((bot) => (
                          <div key={bot.id} className={`bot-option ${selectedBotId === bot.id ? "active" : ""}`} onClick={() => setSelectedBotId(bot.id)}>
                            <img src={bot.avatar} alt={bot.name} className="bot-avatar-small" />
                            <div className="bot-info">
                              <span className="bot-name" style={{ marginBottom: 0 }}>
                                {bot.name}
                              </span>
                            </div>
                            {selectedBotId === bot.id && <div className="check-box">✓</div>}
                          </div>
                        ))}
                      </div>

                      {/* Aviso de Plano Ativo */}
                      {(() => {
                        const selectedBot = bots.find((b) => b.id === selectedBotId);
                        if (selectedBot && selectedBot.apiTier && selectedBot.apiTier !== "free") {
                          const limit = selectedBot.apiTier === "business" ? 100000 : selectedBot.apiTier === "premium" ? 10000 : 1500;
                          const used = selectedBot.apiCallsThisMonth || 0;
                          const remaining = Math.max(0, limit - used);

                          return (
                            <div className="plan-active-warning">
                              <div className="warning-header">
                                <span>⚠️</span> {t("checkout.activePlanWarning.title")}
                              </div>
                              <div className="warning-details">
                                <p className="warning-bot-desc">
                                  <Trans
                                    i18nKey="checkout.activePlanWarning.description"
                                    values={{
                                      botName: selectedBot.name,
                                      apiTier: selectedBot.apiTier,
                                    }}
                                    defaults="O bot <0>{{botName}}</0> está atualmente no plano <1>{{apiTier}}</1>."
                                    components={{
                                      0: <strong />,
                                      1: <strong style={{ textTransform: "uppercase" }} />,
                                    }}
                                  />
                                </p>

                                <div
                                  className="warning-danger-box"
                                  style={{
                                    background: "rgba(240, 71, 71, 0.1)",
                                    border: "1px solid rgba(240, 71, 71, 0.3)",
                                    padding: "1rem",
                                    borderRadius: "8px",
                                    marginBottom: "1rem",
                                    fontSize: "0.9rem",
                                    color: "#f04747",
                                  }}
                                >
                                  <strong>{t("checkout.activePlanWarning.important")}</strong> {t("checkout.activePlanWarning.riskTitle")}
                                  <ul
                                    style={{
                                      marginTop: "0.5rem",
                                      listStyle: "inside disc",
                                    }}
                                  >
                                    <li>
                                      <Trans
                                        i18nKey="checkout.activePlanWarning.risk1"
                                        defaults="Você <0>perderá</0> o restante dos dias já pagos no plano atual."
                                        components={{ 0: <strong /> }}
                                      />
                                    </li>
                                    <li>{t("checkout.activePlanWarning.risk2")}</li>
                                    <li>{t("checkout.activePlanWarning.risk3")}</li>
                                    <li>
                                      <Trans
                                        i18nKey="checkout.activePlanWarning.risk4"
                                        defaults="<0>RISCO DE COBRANÇA DUPLA:</0> Você receberá <1>duas faturas simultâneas</1> no seu cartão nos meses seguintes se não cancelar a assinatura antiga manualmente na Stripe."
                                        components={{
                                          0: <strong />,
                                          1: <strong />,
                                        }}
                                      />
                                    </li>
                                  </ul>
                                </div>

                                <div className="warning-stats-grid">
                                  <div className="warning-stat-card">
                                    <span className="stat-label">{t("checkout.activePlanWarning.statusLabel")}</span>
                                    <span className="stat-value">
                                      {selectedBot.cancelAtPeriodEnd ? t("checkout.activePlanWarning.statusPending") : t("checkout.activePlanWarning.statusActive")}
                                    </span>
                                  </div>
                                  <div className="warning-stat-card">
                                    <span className="stat-label">{t("checkout.activePlanWarning.expiresLabel")}</span>
                                    <span className="stat-value">
                                      {selectedBot.expiresAt ? new Date(selectedBot.expiresAt).toLocaleDateString(t("language") === "en" ? "en-US" : "pt-BR") : "N/A"}
                                    </span>
                                  </div>
                                  <div className="warning-stat-card">
                                    <span className="stat-label">{t("checkout.activePlanWarning.apiTotalLabel")}</span>
                                    <span className="stat-value">{limit.toLocaleString()}</span>
                                  </div>
                                  <div className="warning-stat-card">
                                    <span className="stat-label">{t("checkout.activePlanWarning.remainingLabel")}</span>
                                    <span className="stat-value highlight">{remaining.toLocaleString()}</span>
                                  </div>
                                </div>

                                <div className="warning-footer">{t("checkout.activePlanWarning.footer")}</div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  ) : (
                    <div className="no-bots-warning">
                      <p>{t("checkout.noBotsWarning")}</p>
                      <Link to="/add-bot" className="btn btn-sm btn-outline-primary">
                        {t("checkout.addBotFirst")}
                      </Link>
                    </div>
                  )}
                </div>

                <div className="form-group mt-4">
                  <label>{t("checkout.step2")}</label>
                  <div className="payment-stripe-box">
                    <p>{t("checkout.securePayment")}</p>
                    <small>{t("checkout.paymentDescription")}</small>
                  </div>
                </div>

                {error && <div className="checkout-error">{error}</div>}

                <button type="submit" className="btn btn-primary btn-lg btn-block mt-4" disabled={processing || bots.length === 0}>
                  {processing ? t("checkout.btnPaying") : t("checkout.btnPayNow", { price: tier.price })}
                </button>

                <p className="terms-text">{t("checkout.termsText")}</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
